import { createContext, useContext, useEffect, useRef, useState } from 'react';
import Lenis from '@studio-freight/lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger);

const SmoothScrollContext = createContext(null);

export const useSmoothScroll = () => useContext(SmoothScrollContext);

export function SmoothScrollProvider({ children }) {
  const [lenis, setLenis] = useState(null);
  const reqIdRef = useRef(null);

  useEffect(() => {
    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (prefersReducedMotion) {
      // Skip Lenis for reduced motion
      return;
    }

    // Initialize Lenis with faster, smoother scrolling
    const lenisInstance = new Lenis({
      duration: 0.8,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 1.2,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    setLenis(lenisInstance);

    // Sync Lenis with GSAP ScrollTrigger
    lenisInstance.on('scroll', ScrollTrigger.update);

    // Use GSAP ticker for smooth animation
    gsap.ticker.add((time) => {
      lenisInstance.raf(time * 1000);
    });

    // Disable GSAP lag smoothing for better sync
    gsap.ticker.lagSmoothing(0);

    // Cleanup
    return () => {
      lenisInstance.destroy();
      gsap.ticker.remove(lenisInstance.raf);
    };
  }, []);

  return (
    <SmoothScrollContext.Provider value={lenis}>
      {children}
    </SmoothScrollContext.Provider>
  );
}

// Hook for scroll-triggered animations
export function useScrollReveal(options = {}) {
  const elementRef = useRef(null);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      element.style.opacity = '1';
      element.style.transform = 'none';
      return;
    }

    gsap.fromTo(element, 
      { 
        opacity: 0, 
        y: options.y || 34,
        ...options.from 
      },
      {
        opacity: 1,
        y: 0,
        duration: options.duration || 0.9,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: element,
          start: options.start || 'top 85%',
          toggleActions: 'play none none none',
          ...options.scrollTrigger,
        },
        ...options.to,
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger === element) {
          trigger.kill();
        }
      });
    };
  }, [options]);

  return elementRef;
}

// Hook for scrubbed animations
export function useScrollScrub(options = {}) {
  const containerRef = useRef(null);
  const progressRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const trigger = ScrollTrigger.create({
      trigger: container,
      start: options.start || 'top top',
      end: options.end || 'bottom bottom',
      scrub: options.scrub || true,
      onUpdate: (self) => {
        progressRef.current = self.progress;
        options.onUpdate?.(self.progress);
      },
      ...options.scrollTrigger,
    });

    return () => {
      trigger.kill();
    };
  }, [options]);

  return { containerRef, progressRef };
}

export default SmoothScrollProvider;
